/**
 * pipelineAnalytics — what the pipeline is doing, not just what is in it.
 *
 * ONE HONESTY PROBLEM RAN THROUGH ALL OF THIS, and the fix this file asked for
 * now exists. A deal carries the stage it is in now, and when it dies it
 * carries Passed, Lost or Dead instead of the stage it died from. So a funnel
 * built from current stage alone showed four deals at "LOI Submitted" and
 * pretended the eleven that reached LOI and lost never got there.
 *
 * THE STAGE HISTORY IS THE FIRST SOURCE. activity_log carries a stage_changed
 * row for every move made through the app, plus the 2026-09 backfill that
 * reconstructed the milestones from the deal review. pipelineFunnel takes a
 * stageHistory map — deal id to the deepest LIVE_STAGES index it ever reached —
 * and that beats any inference drawn from leftovers.
 *
 * EVIDENCE STILL FILLS THE GAPS, for deals no history covers, from the facts
 * that survive a death:
 *
 *   time_in_uw_days     it was underwritten
 *   time_to_loi_days    an LOI went out
 *   made_best_and_final it reached best and final, so an LOI certainly went out
 *   market_cleared_*    we were in the process when it traded
 *
 * A dead deal with neither history nor evidence still counts only at the top,
 * so the funnel can understate but never flatter — and it says on its face how
 * many deals are in that position.
 */

// Number(null) and Number("") are both 0, so a bare Number() check turns every
// empty field into a real zero. That is how "median time to LOI: 0 days across
// 613 deals" appeared on screen when four deals record one at all.
const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const ms = (d) => { const t = Date.parse(d); return Number.isFinite(t) ? t : null; };

export const LIVE_STAGES = ["Screening", "Underwriting", "LOI Submitted", "In Diligence"];
export const DEAD_STAGES = ["Passed", "Lost", "Dead"];
/** Not part of the pipeline: passive data and the outreach flow. */
export const OFF_PIPELINE = ["Tracking", "Sourcing", "Outreach", "Unsolicited Offer"];

const isDead = (d) => DEAD_STAGES.includes(d.stage);
const sf = (d) => num(d.squareFeet) || 0;

/**
 * How far a deal can be PROVEN to have travelled, as an index into LIVE_STAGES.
 *
 * `histIdx` is what the stage history says, or -1 when it says nothing. It is
 * the better source, so it wins — but the two are taken together rather than
 * one short-circuiting the other: a deal standing at In Diligence today with
 * only an Underwriting event logged has demonstrably reached In Diligence.
 */
export function reachedIndex(d, histIdx = -1) {
  const live = LIVE_STAGES.indexOf(d.stage);
  if (live >= 0) return Math.max(live, histIdx);
  if (!isDead(d)) return Math.max(-1, histIdx);
  if (histIdx >= 0) return histIdx;
  if (d.madeBestAndFinal || num(d.timeToLoiDays) != null) return 2;   // an LOI went out
  if (num(d.timeInUwDays) != null) return 1;                          // it was underwritten
  if (d.marketClearedPrice || d.marketClearedAt) return 1;            // we tracked it to a trade
  return 0;                                                           // screened, and no more
}

/* ── stage history ────────────────────────────────────────────────── */
/**
 * The deepest LIVE_STAGES index each deal is recorded as having reached, keyed
 * by deal id, from `stage_changed` rows of the activity log. Either side of a
 * change counts — a move OUT of Underwriting proves the deal was there.
 *
 * Shared so that every page drawing the funnel draws the same one. Pipeline and
 * Home both pass it to pipelineFunnel(); a second copy of this loop is how two
 * funnels in the same app came to disagree.
 *
 * Rows without an `action` are assumed pre-filtered to stage_changed; rows with
 * one are filtered here, so a caller may hand over a mixed log.
 */
export function deepestStageIndex(rows = []) {
  const deepest = {};
  for (const row of rows) {
    if (row.action && row.action !== "stage_changed") continue;
    // Both sides, not `new_stage || old_stage`: that read only the new side of
    // any real move, so a deal added straight at Underwriting whose one logged
    // move is Underwriting -> Passed counted as screened and no more.
    for (const stage of [row.metadata?.new_stage, row.metadata?.old_stage]) {
      const i = LIVE_STAGES.indexOf(stage);
      if (i >= 0 && (deepest[row.entity_id] ?? -1) < i) deepest[row.entity_id] = i;
    }
  }
  return deepest;
}

/* ── the funnel ───────────────────────────────────────────────────── */
export function pipelineFunnel(deals = [], stageHistory = {}) {
  const inScope = deals.filter((d) => !OFF_PIPELINE.includes(d.stage));
  // A deal at the DEAD stage with no recorded history is one we were never
  // interested in (Daniel, 2026-09-29) - it reached the top and nothing more,
  // and that is known, not missing. So it counts at the top only, whatever a
  // leftover field might suggest, and it is not reported as an unknown that
  // understates the funnel. A Dead deal that DOES carry history - a logged LOI,
  // say - still counts for what it demonstrably reached.
  const neverInterested = (d) => d.stage === "Dead" && (stageHistory[d.id] ?? -1) < 0;
  const at = (d) => (neverInterested(d) ? 0 : reachedIndex(d, stageHistory[d.id] ?? -1));

  const stages = LIVE_STAGES.map((label, i) => {
    const reached = inScope.filter((d) => at(d) >= i);
    const standing = inScope.filter((d) => d.stage === label);
    const lost = reached.filter((x) => isDead(x) && at(x) === i);
    return {
      label, count: reached.length, sf: reached.reduce((s, d) => s + sf(d), 0),
      standing: standing.length,
      dropped: lost.length,
      value: null,
    };
  });

  const dead = inScope.filter(isDead);
  const fromHistory = dead.filter((d) => (stageHistory[d.id] ?? -1) > 0).length;
  const evidenced = dead.filter((d) => at(d) > 0).length;
  const unevidenced = dead.filter((d) => at(d) === 0 && !neverInterested(d)).length;
  const deadTopOnly = dead.filter(neverInterested).length;

  return {
    stages,
    live: inScope.filter((d) => !isDead(d)).length,
    dead: dead.length,
    evidenced, unevidenced, fromHistory, deadTopOnly,
    how: `Each stage counts deals that can be shown to have REACHED it — those standing in it now, those `
      + `further along, and dead deals we can show got this far. A dead deal is read first from its stage history: `
      + `${fromHistory} of ${dead.length} carr${fromHistory === 1 ? "ies" : "y"} logged stage changes, either made in the app or `
      + `reconstructed from the deal review. Where there is no history we fall back to what survived — a time-to-LOI or best-and-final `
      + `for LOI, a time-in-underwriting or a recorded clearing price for underwriting. `
      + `${unevidenced} closed deal${unevidenced === 1 ? "" : "s"} ha${unevidenced === 1 ? "s" : "ve"} neither and `
      + `count${unevidenced === 1 ? "s" : ""} only at the top, so the funnel can understate conversion but never flatter it. `
      + `${deadTopOnly} deal${deadTopOnly === 1 ? "" : "s"} at Dead with no history count at the top by design - `
      + `deals we were never interested in, not deals whose progress went unrecorded. `
      + `Percentages under each stage are that stage's count over the one before it.`,
  };
}

/* ── deals in and out, by month ───────────────────────────────────── */
export function flowByMonth(deals = [], months = 18) {
  const now = new Date();
  const keys = [];
  for (let i = months - 1; i >= 0; i -= 1) {
    keys.push(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
  }
  const inMonth = (t, k, next) => t != null && t >= k && t < next;

  const points = keys.map((k, i) => {
    const next = i + 1 < keys.length ? keys[i + 1] : Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1);
    const added = deals.filter((d) => inMonth(ms(d.createdAt), k, next));
    const killed = deals.filter((d) => inMonth(ms(d.killedAt), k, next));
    // Live at the end of the month: created by then, not yet dead.
    const live = deals.filter((d) => {
      const c = ms(d.createdAt), x = ms(d.killedAt);
      return c != null && c < next && (x == null || x >= next) && !OFF_PIPELINE.includes(d.stage);
    });
    return {
      x: k, added: added.length, killed: killed.length, live: live.length,
      addedSf: added.reduce((s, d) => s + sf(d), 0),
    };
  });

  const dated = deals.filter((d) => ms(d.createdAt) != null).length;
  return {
    points,
    added: { key: "added", points: points.map((p) => ({ x: p.x, y: p.added })) },
    killed: { key: "killed", points: points.map((p) => ({ x: p.x, y: p.killed })) },
    live: { key: "live", points: points.map((p) => ({ x: p.x, y: p.live })) },
    empty: !dated,
    how: `New deals are counted by the month their record was created and kills by the month they were killed, `
      + `over the last ${months} months. The standing line is how many were open at each month end — created by then and not yet killed, `
      + `excluding tracking and outreach records. ${deals.length - dated} deal${deals.length - dated === 1 ? "" : "s"} carry no creation date and appear nowhere on this chart.`,
  };
}

/* ── where deals die ──────────────────────────────────────────────── */
export function killMatrix(deals = []) {
  const dead = deals.filter((d) => isDead(d) && d.killedReason);
  if (!dead.length) return { empty: true, how: "No killed deal carries a recorded reason." };

  const reasons = [...new Set(dead.map((d) => d.killedReason))];
  const cols = DEAD_STAGES.filter((s) => dead.some((d) => d.stage === s));
  const cells = [];
  reasons.forEach((r) => cols.forEach((c) => {
    const hit = dead.filter((d) => d.killedReason === r && d.stage === c);
    if (!hit.length) return;
    cells.push({
      row: r, col: c, value: hit.length, n: 0,
      note: `${Math.round(hit.reduce((s, d) => s + sf(d), 0)).toLocaleString()} SF`,
    });
  }));

  // Ordered by how much they cost us, not alphabetically.
  const byCount = reasons
    .map((r) => ({ reason: r, n: dead.filter((d) => d.killedReason === r).length,
                   sf: dead.filter((d) => d.killedReason === r).reduce((s, d) => s + sf(d), 0) }))
    .sort((a, b) => b.n - a.n);

  return {
    empty: false, rows: byCount.map((b) => b.reason), cols, cells, ranked: byCount,
    missing: deals.filter((d) => isDead(d) && !d.killedReason).length,
    how: `Every killed deal carrying a reason, counted against the terminal stage it was moved to — `
      + `Passed is our decision, Lost is a competition we entered and did not win, Dead is an opportunity that closed. `
      + `${deals.filter((d) => isDead(d) && !d.killedReason).length} killed deal${deals.filter((d) => isDead(d) && !d.killedReason).length === 1 ? "" : "s"} carry no reason and appear nowhere here. `
      + `Hovering a cell shows the area behind it, which is usually the more interesting number.`,
  };
}

/* ── cycle times ──────────────────────────────────────────────────── */
export function cycleTimes(deals = []) {
  const uw = deals.map((d) => num(d.timeInUwDays)).filter((v) => v != null && v >= 0);
  const loi = deals.map((d) => num(d.timeToLoiDays)).filter((v) => v != null && v >= 0);
  const med = (a) => {
    if (!a.length) return null;
    const s = [...a].sort((x, y) => x - y);
    const m = Math.floor(s.length / 2);
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
  };
  return {
    uwDays: med(uw), uwN: uw.length,
    loiDays: med(loi), loiN: loi.length,
    empty: !uw.length && !loi.length,
    how: `Medians rather than means, across the ${uw.length} deal${uw.length === 1 ? "" : "s"} carrying a time in underwriting `
      + `and the ${loi.length} carrying a time to LOI. One deal that sat for two years should not move the figure, and with a `
      + `sample this size a mean would let it.`,
  };
}

/* ── where the live pipeline sits ─────────────────────────────────── */
/**
 * Live building area by MSA, ranked. Answers "are we all in one market?" —
 * which is a portfolio question the funnel cannot ask.
 *
 * A deal spanning two MSAs contributes its FULL area to each, because half a
 * building in each is a fiction. That makes the bars sum to more than the
 * pipeline when such a deal exists, so `share` is measured against the real
 * live total and `overlap` reports how many deals are double-counted. The bar
 * widths are relative to the largest MSA, not to the total, for the same
 * reason.
 */
export function marketShare(deals = []) {
  const inScope = deals.filter((d) => !OFF_PIPELINE.includes(d.stage));
  const live = inScope.filter((d) => !isDead(d));
  const totalSf = live.reduce((s, d) => s + sf(d), 0);

  const byMsa = new Map();
  for (const d of live) {
    const list = Array.isArray(d.msas) && d.msas.length ? d.msas : ["Unassigned"];
    for (const m of list) {
      const cur = byMsa.get(m) || { msa: m, sf: 0, n: 0 };
      cur.sf += sf(d);
      cur.n += 1;
      byMsa.set(m, cur);
    }
  }

  const ranked = [...byMsa.values()]
    .map((r) => ({ ...r, share: totalSf ? r.sf / totalSf : null }))
    .sort((a, b) => b.sf - a.sf || b.n - a.n);

  const overlap = live.filter((d) => Array.isArray(d.msas) && d.msas.length > 1).length;
  const unassigned = live.filter((d) => !Array.isArray(d.msas) || !d.msas.length).length;
  const noArea = live.filter((d) => !sf(d)).length;

  return {
    ranked,
    totalSf,
    liveN: live.length,
    overlap,
    unassigned,
    noArea,
    empty: !ranked.length || !totalSf,
    top: ranked[0] || null,
    how: `Building area across the ${live.length} live deal${live.length === 1 ? "" : "s"}, grouped by the MSAs each one is `
      + `tagged with. Percentages are of the ${Math.round(totalSf).toLocaleString()} SF live in total. `
      + (overlap
        ? `${overlap} deal${overlap === 1 ? " spans" : "s span"} more than one MSA and count${overlap === 1 ? "s" : ""} in full under each, `
          + `so the bars sum to more than 100% — half a building in each market would be a fiction. `
        : ``)
      + (unassigned ? `${unassigned} carr${unassigned === 1 ? "ies" : "y"} no MSA at all and sit under Unassigned. ` : ``)
      + (noArea ? `${noArea} carr${noArea === 1 ? "ies" : "y"} no area and add nothing to any bar. ` : ``)
      + `Dead deals are excluded: this is where our money is pointed now, not where it has been.`,
  };
}

/* ── headline measures ────────────────────────────────────────────── */
/** The return we underwrite to. Used only to tone the median, never to filter. */
export const IRR_BAR = 15;

export function pipelineKpis(deals = []) {
  const inScope = deals.filter((d) => !OFF_PIPELINE.includes(d.stage));
  const live = inScope.filter((d) => !isDead(d));

  // A zero asking price is an empty field stored as 0, not a price — seven
  // live deals carry one today. Counting them as priced reads "priced on 17
  // of 17" while seven of those tiles show nothing, so the test is > 0 and
  // not merely non-null.
  const priced = live.filter((d) => num(d.askingPrice) > 0);

  // `marketed` defaults to false in dbToDeal, so an unpopulated column would
  // read as off-market and flatter this figure. Every live deal carries a real
  // boolean today — the denominator rides on the tile so the day that stops
  // being true is visible rather than silent.
  const offMarket = live.filter((d) => !d.marketed);

  // Returns are stored as PERCENT (15.82, never 0.1582). An un-underwritten
  // deal carries null, and Number(null) is 0, which would drag a median
  // through the floor — num() screens those out rather than counting them.
  const irrs = live
    .map((d) => num(d.leveredIrr))
    .filter((v) => v != null && v !== 0)
    .sort((a, b) => a - b);
  const mid = Math.floor(irrs.length / 2);
  const medianIrr = irrs.length
    ? (irrs.length % 2 ? irrs[mid] : (irrs[mid - 1] + irrs[mid]) / 2)
    : null;

  return {
    live: live.length,
    liveSf: live.reduce((s, d) => s + sf(d), 0),
    liveValue: priced.reduce((s, d) => s + num(d.askingPrice), 0),
    valueKnown: priced.length,
    offMarket: offMarket.length,
    offMarketShare: live.length ? offMarket.length / live.length : null,
    medianIrr,
    irrKnown: irrs.length,
    how: {
      live: `Deals not in a terminal stage, excluding tracking records and the outreach flow — those are properties we hold `
        + `data on and offers we fired, not deals we are working.`,
      value: `Asking prices summed across the ${priced.length} live deal${priced.length === 1 ? "" : "s"} that carry one, out of `
        + `${live.length}. It is a gross figure for what is in front of us — not equity, not a weighted pipeline value, and not a forecast.`,
      offMarket: `Live deals whose \`marketed\` flag is false, over all ${live.length} live deal${live.length === 1 ? "" : "s"}. `
        + `The flag is a plain boolean with no null state, so a deal nobody has classified counts as off-market. `
        + `${live.filter((d) => d.marketed).length} ${live.filter((d) => d.marketed).length === 1 ? "is" : "are"} marked as marketed.`,
      irr: `Median levered IRR across the ${irrs.length} live deal${irrs.length === 1 ? "" : "s"} that have been underwritten, out of `
        + `${live.length}. Stored as a percent, so 15.8 means 15.8%. Deals carrying no return are excluded rather than counted as zero. `
        + `Green at or above the ${IRR_BAR}% we underwrite to, amber below — the bar is for colour only and filters nothing.`,
    },
  };
}
